import React, { useEffect, useState, useCallback, useRef } from "react";
import { Spinner } from "react-bootstrap";
import { GoogleLogin as GoogleLoginButton } from "@react-oauth/google";
import apiClient from "../../utils/apiClient";
import useGoogleAuth from "../../hooks/useGoogleAuth";
import {
  Shield, Smartphone, Mail, CheckCircle2, AlertCircle, Info,
  Lock, Eye, EyeOff, Link2, Unlink,
} from "lucide-react";
import { AppButton, GoogleIcon } from "../ui";

const MSG_AUTO_CLEAR_MS = 6000;

/**
 * 2FA + Google UI embedded inside the main Security card on Account Settings.
 */
const SecuritySettings = () => {
  const [tfa, setTfa] = useState({ enabled: false, method: null, hasPhone: false, hasEmail: false });
  const [google, setGoogle] = useState({ linked: false });
  const [loading, setLoading] = useState(true);

  const [setupMethod, setSetupMethod] = useState(null);
  const [setupOtp, setSetupOtp] = useState("");
  const [setupSending, setSetupSending] = useState(false);
  const [setupVerifying, setSetupVerifying] = useState(false);
  const [setupSent, setSetupSent] = useState(false);

  const [disableMode, setDisableMode] = useState(false);
  const [disablePw, setDisablePw] = useState("");
  const [disableShowPw, setDisableShowPw] = useState(false);
  const [disabling, setDisabling] = useState(false);

  const [googleLoading, setGoogleLoading] = useState(false);

  const [msg, setMsg] = useState({ type: "", text: "" });
  const msgTimerRef = useRef(null);

  const clearMsgTimer = () => {
    if (msgTimerRef.current) {
      clearTimeout(msgTimerRef.current);
      msgTimerRef.current = null;
    }
  };

  const dismissMsg = useCallback(() => {
    clearMsgTimer();
    setMsg({ type: "", text: "" });
  }, []);

  useEffect(() => () => clearMsgTimer(), []);

  useEffect(() => {
    clearMsgTimer();
    if (!msg.text || msg.type === "error") return;
    msgTimerRef.current = setTimeout(() => {
      setMsg({ type: "", text: "" });
      msgTimerRef.current = null;
    }, MSG_AUTO_CLEAR_MS);
    return clearMsgTimer;
  }, [msg.text, msg.type]);

  const handleGoogleCredentialFromPopup = useCallback(async (credential) => {
    setGoogleLoading(true);
    setMsg({ type: "", text: "" });
    try {
      const { data } = await apiClient.post("/auth/google/link", { idToken: credential });
      setGoogle({ linked: true });
      setMsg({ type: "success", text: data.message });
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.message || "Failed to link Google." });
    } finally {
      setGoogleLoading(false);
    }
  }, []);

  const onGoogleLinkError = useCallback((errMsg) => {
    setMsg({ type: "error", text: typeof errMsg === "string" ? errMsg : "Google linking failed." });
  }, []);

  const { isDirectMode: googleDirect, openPopup: openGooglePopup, hasGoogleAuth } = useGoogleAuth(
    handleGoogleCredentialFromPopup,
    onGoogleLinkError
  );

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const [tfaRes, googleRes] = await Promise.all([
        apiClient.get("/auth/2fa/status"),
        apiClient.get("/auth/google/status"),
      ]);
      const t = tfaRes.data;
      setTfa({
        enabled: !!t.twoFactorEnabled,
        method: t.twoFactorMethod || null,
        hasPhone: !!t.hasPhone,
        hasEmail: !!t.hasEmail,
      });
      const g = googleRes.data;
      setGoogle({ linked: !!g.googleLinked });
    } catch {
      // non-critical
    } finally {
      setLoading(false);
    }
  };

  const handleStartSetup = async (method) => {
    setSetupMethod(method);
    setSetupOtp("");
    setSetupSending(true);
    setSetupSent(false);
    setMsg({ type: "", text: "" });
    try {
      const { data } = await apiClient.post("/auth/2fa/setup", { method });
      setSetupSent(true);
      setMsg({ type: "info", text: data.message });
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.message || "Failed to send OTP." });
      setSetupMethod(null);
    } finally {
      setSetupSending(false);
    }
  };

  const handleConfirmSetup = async (e) => {
    e.preventDefault();
    if (!setupOtp || setupOtp.length < 6) return;
    setSetupVerifying(true);
    setMsg({ type: "", text: "" });
    try {
      const { data } = await apiClient.post("/auth/2fa/confirm-setup", {
        method: setupMethod,
        otp: setupOtp,
      });
      setTfa((prev) => ({ ...prev, enabled: true, method: setupMethod }));
      setMsg({ type: "success", text: data.message });
      setSetupMethod(null);
      setSetupOtp("");
      setSetupSent(false);
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.message || "Verification failed." });
    } finally {
      setSetupVerifying(false);
    }
  };

  const handleDisable2FA = async (e) => {
    e.preventDefault();
    if (!disablePw) return;
    setDisabling(true);
    setMsg({ type: "", text: "" });
    try {
      const { data } = await apiClient.post("/auth/2fa/disable", { password: disablePw });
      setTfa((prev) => ({ ...prev, enabled: false, method: null }));
      setMsg({ type: "success", text: data.message });
      setDisableMode(false);
      setDisablePw("");
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.message || "Failed to disable 2FA." });
    } finally {
      setDisabling(false);
    }
  };

  const handleGoogleLink = async (credentialResponse) => {
    setGoogleLoading(true);
    setMsg({ type: "", text: "" });
    try {
      const { data } = await apiClient.post("/auth/google/link", {
        idToken: credentialResponse.credential,
      });
      setGoogle({ linked: true });
      setMsg({ type: "success", text: data.message });
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.message || "Failed to link Google." });
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleUnlink = async () => {
    setGoogleLoading(true);
    setMsg({ type: "", text: "" });
    try {
      const { data } = await apiClient.post("/auth/google/unlink");
      setGoogle({ linked: false });
      setMsg({ type: "success", text: data.message });
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.message || "Failed to unlink Google." });
    } finally {
      setGoogleLoading(false);
    }
  };

  const msgIcon = () => {
    if (msg.type === "success") return <CheckCircle2 size={18} aria-hidden />;
    if (msg.type === "error") return <AlertCircle size={18} aria-hidden />;
    return <Info size={18} aria-hidden />;
  };

  if (loading) {
    return (
      <div className="acct-security-embed acct-security-embed--loading">
        <Spinner animation="border" size="sm" />
        <span>Loading security options…</span>
      </div>
    );
  }

  return (
    <div className="acct-security-embed">
      {msg.text && (
        <div
          className={`acct-security-embed__banner acct-alert acct-alert--${msg.type === "info" ? "info" : msg.type}`}
          role="status"
        >
          {msgIcon()}
          <span className="acct-security-embed__banner-text">{msg.text}</span>
          <button type="button" className="acct-security-embed__banner-dismiss" onClick={dismissMsg} aria-label="Dismiss">
            ×
          </button>
        </div>
      )}

      <section className="acct-security-embed__section" aria-labelledby="acct-2fa-heading">
        <div className="acct-security-embed__section-head">
          <h4 id="acct-2fa-heading" className="acct-security-embed__section-title">
            <Shield size={17} strokeWidth={2} /> Two-factor authentication
          </h4>
          {tfa.enabled && (
            <span className="acct-badge acct-badge--success">Enabled</span>
          )}
        </div>
        <div className="acct-security-embed__section-body">
          {tfa.enabled && !disableMode && !setupMethod ? (
            <div className="acct-security-view">
              <div className="acct-security-view__row">
                <div>
                  <p className="acct-security-embed__lead">
                    {tfa.method === "sms" ? (
                      <><Smartphone size={15} className="acct-security-embed__inline-icon" /> SMS verification</>
                    ) : (
                      <><Mail size={15} className="acct-security-embed__inline-icon" /> Email verification</>
                    )}
                  </p>
                  <p className="acct-security-view__desc">
                    A code is sent to your {tfa.method === "sms" ? "phone" : "email"} when you sign in.
                  </p>
                </div>
                <AppButton variant="ghost" size="sm" onClick={() => setDisableMode(true)}>
                  Disable
                </AppButton>
              </div>
            </div>
          ) : disableMode ? (
            <form className="acct-form" onSubmit={handleDisable2FA}>
              <p className="acct-security-view__desc acct-security-view__desc--tight">
                Enter your password to turn off two-factor authentication.
              </p>
              <div className="acct-form__group">
                <label className="acct-form__label">Password</label>
                <div className="acct-form__input-wrap">
                  <Lock size={16} className="acct-form__input-icon" />
                  <input
                    className="acct-form__input"
                    type={disableShowPw ? "text" : "password"}
                    value={disablePw}
                    onChange={(e) => setDisablePw(e.target.value)}
                    required
                  />
                  <button type="button" className="acct-form__eye" onClick={() => setDisableShowPw(!disableShowPw)}>
                    {disableShowPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div className="acct-form__actions">
                <AppButton type="submit" variant="danger" disabled={disabling}>
                  {disabling ? <Spinner size="sm" animation="border" /> : "Disable 2FA"}
                </AppButton>
                <AppButton variant="secondary" type="button" onClick={() => { setDisableMode(false); setDisablePw(""); }}>
                  Cancel
                </AppButton>
              </div>
            </form>
          ) : setupMethod && setupSent ? (
            <form className="acct-form" onSubmit={handleConfirmSetup}>
              <p className="acct-security-view__desc acct-security-view__desc--tight">
                Enter the 6-digit code sent to your {setupMethod === "sms" ? "phone" : "email"}.
              </p>
              <div className="acct-form__group">
                <label className="acct-form__label">Verification code</label>
                <div className="acct-form__input-wrap">
                  <Shield size={16} className="acct-form__input-icon" />
                  <input
                    className="acct-form__input"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="6-digit code"
                    value={setupOtp}
                    onChange={(e) => setSetupOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    required
                    autoFocus
                  />
                </div>
              </div>
              <div className="acct-form__actions">
                <AppButton type="submit" disabled={setupVerifying || setupOtp.length < 6}>
                  {setupVerifying ? <Spinner size="sm" animation="border" /> : <CheckCircle2 size={14} />}
                  Verify &amp; enable
                </AppButton>
                <AppButton variant="secondary" type="button" onClick={() => { setSetupMethod(null); setSetupOtp(""); setSetupSent(false); setMsg({ type: "", text: "" }); }}>
                  Cancel
                </AppButton>
              </div>
            </form>
          ) : (
            <div className="acct-security-view">
              <p className="acct-security-view__desc acct-security-view__desc--tight">
                Add a verification step when you sign in (SMS or email).
              </p>
              <div className="acct-security-embed__btn-row">
                {tfa.hasPhone && (
                  <AppButton variant="outline" size="sm" onClick={() => handleStartSetup("sms")} disabled={setupSending}>
                    {setupSending && setupMethod === "sms" ? <Spinner size="sm" animation="border" /> : <Smartphone size={14} />}
                    SMS
                  </AppButton>
                )}
                {tfa.hasEmail && (
                  <AppButton variant="outline" size="sm" onClick={() => handleStartSetup("email")} disabled={setupSending}>
                    {setupSending && setupMethod === "email" ? <Spinner size="sm" animation="border" /> : <Mail size={14} />}
                    Email
                  </AppButton>
                )}
                {!tfa.hasPhone && !tfa.hasEmail && (
                  <p className="acct-security-view__desc">Add phone or email under Personal information to enable 2FA.</p>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {hasGoogleAuth && (
        <>
          <div className="acct-security-embed__divider" role="separator" />
          <section className="acct-security-embed__section" aria-labelledby="acct-google-heading">
            <div className="acct-security-embed__section-head">
              <h4 id="acct-google-heading" className="acct-security-embed__section-title">
                <GoogleIcon className="acct-security-embed__google-icon" /> Google account
              </h4>
              {google.linked && (
                <span className="acct-badge acct-badge--success">Connected</span>
              )}
            </div>
            <div className="acct-security-embed__section-body">
              {google.linked ? (
                <div className="acct-security-view">
                  <div className="acct-security-view__row">
                    <div>
                      <p className="acct-security-embed__lead">
                        <Link2 size={15} className="acct-security-embed__inline-icon" /> Linked
                      </p>
                      <p className="acct-security-view__desc">You can sign in with Google. Unlink to use password only.</p>
                    </div>
                    <AppButton variant="ghost" size="sm" onClick={handleGoogleUnlink} disabled={googleLoading}>
                      {googleLoading ? <Spinner size="sm" animation="border" /> : <Unlink size={14} />}
                      Unlink
                    </AppButton>
                  </div>
                </div>
              ) : (
                <div className="acct-security-view">
                  <p className="acct-security-view__desc acct-security-view__desc--tight">
                    Link Google for quick sign-in. Google email must match this account.
                  </p>
                  {googleLoading ? (
                    <div className="acct-security-embed--loading acct-security-embed--loading-inline">
                      <Spinner size="sm" animation="border" /> Linking…
                    </div>
                  ) : googleDirect ? (
                    <div className="acct-google-btn-wrap acct-google-btn-wrap--embed">
                      <GoogleLoginButton
                        onSuccess={handleGoogleLink}
                        onError={() => setMsg({ type: "error", text: "Google linking failed." })}
                        text="continue_with"
                        shape="rectangular"
                        theme="outline"
                      />
                    </div>
                  ) : (
                    <AppButton variant="outline" size="sm" onClick={openGooglePopup}>
                      <GoogleIcon size={14} /> Link Google
                    </AppButton>
                  )}
                </div>
              )}
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default SecuritySettings;
